import { useState } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import {
  Expander,
  ExpanderChevronPosition,
  ExpanderLayout,
} from '@amzn/stencil-react-components/expander';
import { IconPersonSmall } from '@amzn/stencil-react-icons';
import { MetadataGrid } from './MetadataGrid';

// ─── Cell Types ──────────────────────────────────────────────────────────────

/** A text-only metadata cell. */
export interface SummaryTextCell {
  key: string;
  show: boolean;
  badge?: false;
  assignees?: false;
  label: string;
  value: string;
}

/** A badge metadata cell. */
export interface SummaryBadgeCell {
  key: string;
  show: boolean;
  badge: true;
  assignees?: false;
  label: string;
  badgeType: BadgeType;
  badgeValue: string;
}

/**
 * An assignees metadata cell — renders the shared `AssigneesDisplay`
 * primitive so the "1 person / N people (+M more)" collapsing logic
 * stays in one place. Use this instead of a text cell for any
 * multi-assignee value (audit assignees, task followers, etc.) so a
 * single-string fallback doesn't quietly drop the extras.
 */
export interface SummaryAssigneesCell {
  key: string;
  show: boolean;
  assignees: true;
  badge?: false;
  label: string;
  assigneesList: readonly string[];
  /** Text to show when `assigneesList` is empty. */
  emptyLabel: string;
}

/** Union of all cell types supported by SummaryExpander. */
export type SummaryCellConfig = SummaryTextCell | SummaryBadgeCell | SummaryAssigneesCell;

// ─── Component ───────────────────────────────────────────────────────────────

interface SummaryExpanderProps {
  /** Title shown in the expander header. */
  titleText: string;
  /** Array of cell configurations to render in the MetadataGrid. */
  cells: SummaryCellConfig[];
  /** Whether the expander starts expanded. Defaults to true. */
  defaultExpanded?: boolean;
  /**
   * Fixed column count for the grid. Omit on mobile, where the grid fits as
   * many cells as the viewport allows. Set it on a desktop surface, where
   * that same rule gives eight columns and lays a short field list out as
   * one long row.
   */
  columns?: number;
}

/**
 * Generic expandable summary card showing metadata in a responsive CSS grid.
 *
 * Used by AuditSummaryExpander and TaskSummaryExpander — keeps the
 * Expander + MetadataGrid + cell rendering in one shared place.
 *
 * Callers build the `cells` array with domain-specific logic, then
 * hand it off to this component for rendering.
 */
export function SummaryExpander({
  titleText,
  cells,
  defaultExpanded = true,
  columns,
}: SummaryExpanderProps): JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const visible = cells.filter((cell) => cell.show);

  return (
    <Expander
      titleText={titleText}
      chevronPosition={ExpanderChevronPosition.Trailing}
      isExpanded={expanded}
      onToggle={setExpanded}
      layout={ExpanderLayout.Card}
    >
      {/* `exactOptionalPropertyTypes` rejects an explicit `undefined` for an
          optional prop, so the omitted case has to be a separate element. */}
      {columns === undefined ? (
        <MetadataGrid>{visible.map(renderCell)}</MetadataGrid>
      ) : (
        <MetadataGrid columns={columns}>{visible.map(renderCell)}</MetadataGrid>
      )}
    </Expander>
  );
}

// ─── Internal Cell Components ────────────────────────────────────────────────

/**
 * Renders a single visible cell based on its discriminator. Extracted
 * from the JSX map so the inline `.map(renderCell)` above stays
 * one-liner-shaped and TS narrows on the three variants without a
 * nested ternary.
 */
function renderCell(cell: SummaryCellConfig): JSX.Element {
  if (cell.badge) {
    return (
      <BadgeCell
        key={cell.key}
        label={cell.label}
        badgeType={cell.badgeType}
        badgeValue={cell.badgeValue}
      />
    );
  }
  if (cell.assignees) {
    return (
      <AssigneesCell
        key={cell.key}
        label={cell.label}
        assigneesList={cell.assigneesList}
        emptyLabel={cell.emptyLabel}
      />
    );
  }
  return <TextCell key={cell.key} label={cell.label} value={cell.value} />;
}

function TextCell({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Col>
      <Text color="color.text.secondary">{label}</Text>
      <Text fontWeight="bold">{value}</Text>
    </Col>
  );
}

function BadgeCell({
  label,
  badgeType,
  badgeValue,
}: {
  label: string;
  badgeType: BadgeType;
  badgeValue: string;
}): JSX.Element {
  return (
    <Col>
      <Text color="color.text.secondary">{label}</Text>
      <Badge type={badgeType} value={badgeValue} />
    </Col>
  );
}

function AssigneesCell({
  label,
  assigneesList,
  emptyLabel,
}: {
  label: string;
  assigneesList: readonly string[];
  emptyLabel: string;
}): JSX.Element {
  // Summary panels intentionally render every alias — collapsing to
  // "alice (+N-1)" leaves the reader with no way to discover who else
  // is on the audit. Matches V1's audit detail (`AssigneeAvatars` shows
  // every avatar side-by-side, no overflow). The compact
  // `AssigneesDisplay` variant is still used on space-constrained
  // surfaces like list cards.
  const hasAssignees = assigneesList.length > 0;
  return (
    <Col>
      <Text color="color.text.secondary">{label}</Text>
      {hasAssignees ? (
        <Row alignItems="center" gridGap="dimensions.spacing.100" flexWrap="wrap">
          <IconPersonSmall color="color.text.primary" aria-hidden />
          <Text fontWeight="bold" color="color.text.primary">
            {assigneesList.join(', ')}
          </Text>
        </Row>
      ) : (
        <Text fontWeight="bold">{emptyLabel}</Text>
      )}
    </Col>
  );
}
